import React from 'react'
import pick from 'lodash/pick'

import lang from '@/lang'
import Header from '@/modules/base/components/modal/upgrade-modal/header/header'
import styles from './order-overview.css'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import settingsConfig from '@/modules/settings/config'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import ModalFooter from '@/modules/base/components/modal/footer/modal-footer'
import OrderOverviewItem from './item/order-overview-item'
import IconCheck from '@assets/images/icons/check.svg'
import CancelButton from '@/modules/base/components/buttons/cancel-button/cancel-button'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import getTexts from './_helpers/getTexts'
import { LogoutButton } from '@/modules/base/components/buttons/index'
import { SubscriptionTitle } from '@/modules/base/components/modal/upgrade-modal/subscription-title'
import { analyticsService } from '@/modules/base/services/analytics'
import CouponCodeInput from './coupon-code-input/coupon-code-input'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'

import { toast } from '@/modules/base/components/toast/toastConfig'

const PERIOD_LABELS = {
  month: 'pro Monat',
  year: 'pro Jahr',
}
const REQUEST_PROPS = [
  'email',
  'companyName',
  'salutation',
  'firstName',
  'lastName',
  'street',
  'zipCode',
  'city',
  'paymentType',
  'bankAccountHolder',
  'bankAccountIBAN',
  'bankAccountBic',
  'couponCode',
]

class OrderOverview extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      couponCode: null,
      isLoading: false,
    }

    this.onNextClicked = this.onNextClicked.bind(this)
    this.continueUpgradeAsDemo = this.continueUpgradeAsDemo.bind(this)
    this.upgradeSubscription = this.upgradeSubscription.bind(this)
    this.onCouponCodeValidate = this.onCouponCodeValidate.bind(this)
  }

  componentDidUpdate() {
    const { couponCode } = this.state
    const { data } = this.props

    // reset coupon code when period changes and coupon code is only valid for one period type
    if (couponCode && couponCode.period && data?.paymentInterval?.period !== couponCode.period) {
      this.setState({ couponCode: null })
    }
  }

  continueUpgradeAsDemo() {
    request(settingsConfig.upsertDemoTenant, { auth: true, method: 'POST' })
      .then(response => {
        const { body: { data: { loginToken } } } = response
        user.token = loginToken
        user.isDemo = false
        return response
      })
      .then(this.upgradeSubscription)
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }

  upgradeSubscription() {
    const { onNext, data } = this.props
    const { couponCode } = this.state
    const {
      paymentInterval,
      subscription: { name: subscriptionType },
    } = data

    this.setState({ isLoading: true })

    if (couponCode?.code) {
      data.couponCode = couponCode.code
    }

    const requestData = {
      ...pick(data, REQUEST_PROPS),
      price: paymentInterval,
      subscriptionType,
    }

    return request(settingsConfig.subscription, { auth: true, method: 'PUT', data: requestData })
      .then(() => {
        const trackingData = {
          price: paymentInterval.pricePerDuration,
          type: subscriptionType,
        }
        analyticsService.trackPurchase(trackingData)
        WebStorageService.removeItem(WebStorageKey.READ_ONLY_CONFIRMED)
        onNext()
      })
      .catch((error) => {
        if (error && error.body && error.body.meta
            && error.body.meta.subscriptionType
            && error.body.meta.subscriptionType[0].code
            && error.body.meta.subscriptionType[0].code === 'IS_MOBILE') {
          toast.showToast({ type: 'error', message: lang.upgradeSubscriptionErrorIsMobile })
        } else {
          toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        }
      })
      .finally(() => {
        this.setState({ isLoading: false })
      })
  }

  onNextClicked() {
    if (user.isDemo) {
      return this.continueUpgradeAsDemo()
    }

    return this.upgradeSubscription()
  }

  onCouponCodeValidate(couponCode) {
    this.setState({ couponCode })
  }

  render() {
    const { trialExpired, data, onCancel, onLogout, onPrevious } = this.props
    const { couponCode, isLoading } = this.state

    const cancelButton = trialExpired
      ? (
        <LogoutButton onClick={onLogout} />
      )
      : (
        <CancelButton label="Abbrechen" onClick={onCancel} />
      )

    if (!data) return null

    const {
      subscription,
      paymentInterval,
      bankAccountHolder,
      bankAccountIBAN,
      bankAccountBic,
      companyName,
      salutation,
      firstName,
      lastName,
      email,
      street,
      zipCode,
      city,
    } = data

    if (!subscription || !paymentInterval) return null

    const contactName = [salutation, firstName, lastName].join(' ')
    const { title, description, type } = subscription
    const { period, pricePerDuration, pricePerDurationAfterDiscount } = paymentInterval
    const { agbUrl, periodInfo, periodHint } = getTexts(type)
    const periodHintEl = periodHint ? <span className={styles.periodHint}>{periodHint}</span> : null

    const descriptionElm = description?.split('\n').map((line, index) => <span key={index}>{line}</span>)

    return (
      <div className={styles.container}>
        <Header title="Deine Bestellübersicht" />
        <div className={styles.content}>
          <div className={styles.row}>
            <OrderOverviewItem customClass={styles.overviewItemHighlighted}>
              <div className={styles.subscription}>
                <div className={styles.subscriptionContainer}>
                  <span className={styles.title}>
                    {lang.upgradeSubscriptionOrderOverviewSubscriptionTitle}
                  </span>
                  <div className={styles.subscriptionDetailContent}>
                    <span className={styles.highlighted}>
                      <SubscriptionTitle title={title} className={styles.subscriptionTitle} />
                    </span>
                    <span className={`${styles.subtext} ${styles.primary}  ${styles.description}`}>{descriptionElm}</span>
                  </div>
                </div>
                <div className={styles.priceContainer}>
                  <span className={styles.title}>
                    {lang.upgradeSubscriptionOrderOverviewPriceTitle}
                  </span>
                  <div className={styles.subscriptionDetailContent}>
                    <div className={styles.highlighted}>
                      {`${formatCurrency(pricePerDurationAfterDiscount || pricePerDuration)} ${PERIOD_LABELS[period]}`}
                    </div>
                    {couponCode && (
                      <div className={styles.subtext}>
                        <div>{lang.upgradeModal.coupon}: <span className="text-semibold">{couponCode.code}</span></div>
                        <div>
                          {lang.upgradeModal.minusDiscount}:
                          <span className="text-semibold">
                            {' '}{couponCode.type === 'amount'
                              ? formatCurrency(couponCode.amount)
                              : formatCurrency(couponCode.amount / 100 * (pricePerDurationAfterDiscount || pricePerDuration)) }
                          </span>
                        </div>
                      </div>
                    )}
                    <span className={styles.subtext}>{lang.upgradeModal.vatHint}</span>
                  </div>
                </div>
                <div className={styles.periodContainer}>
                  <span className={styles.title}>
                    {lang.upgradeSubscriptionOrderOverviewPeriodTitle}
                  </span>
                  <div className={styles.subscriptionDetailContent}>
                    <span className={styles.highlighted}>{periodInfo}</span>
                    {periodHintEl}
                  </div>
                </div>
              </div>
            </OrderOverviewItem>
          </div>
          <div className={styles.row}>
            <OrderOverviewItem>
              <div className={styles.smallBoxContentWrapper}>
                <span className={styles.title}>
                  {lang.upgradeSubscriptionOrderOverviewBillingTitle}
                </span>
                <div className={styles.smallBoxContent}>
                  <span>{companyName}</span>
                  <span>{street}</span>
                  <span>{`${zipCode} ${city}`}</span>
                </div>
              </div>
            </OrderOverviewItem>
            <OrderOverviewItem>
              <div className={styles.smallBoxContentWrapper}>
                <span className={styles.title}>
                  {lang.upgradeSubscriptionOrderOverviewContactTitle}
                </span>
                <div className={styles.smallBoxContent}>
                  <span>{contactName}</span>
                  <span>{email}</span>
                </div>
              </div>
            </OrderOverviewItem>
            <OrderOverviewItem>
              <div className={styles.smallBoxContentWrapper}>
                <span className={styles.title}>
                  {lang.upgradeSubscriptionOrderOverviewPaymentTitle}
                </span>
                <div className={styles.smallBoxContent}>
                  <span>{bankAccountHolder}</span>
                  <span>{bankAccountIBAN}</span>
                  <span>{bankAccountBic}</span>
                </div>
              </div>
            </OrderOverviewItem>
          </div>
          <div className={styles.agbCouponCodeWrapper}>
            <label className={styles.agbLabel}>
            Es gelten unsere{' '}
              <a href={agbUrl} rel="noopener noreferrer" target="_blank">
              AGB
              </a>
              .
            </label>
            <CouponCodeInput
              disabled={!!couponCode}
              disabledHint={lang.upgradeModal.couponCodeDisabledHint}
              period={period}
              subscriptionType={subscription?.name}
              onCouponCodeValidate={this.onCouponCodeValidate} />
          </div>
        </div>
        <ModalFooter onBackButtonClicked={onPrevious}>
          {cancelButton}
          <ButtonIconSquare
            label={lang.upgradeSubscriptionOrderOverviewBuyButtonLabel}
            icon={IconCheck}
            customClass={styles.continueButton}
            onClick={this.onNextClicked}
            disabled={isLoading}
          />
        </ModalFooter>
      </div>
    )
  }
}

export default OrderOverview
